import React from "react";

/* ── SVG Icons ── */
function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

/**
 * LineItemsEditor
 * ─────────────────────────────────────────────────────────────
 * Reusable Parent-Child Line Items Manager:
 * - Single Top Entry Form (active for adding or editing)
 * - Compact Bottom Grid/Table showing all added line items
 * - Clean horizontal actions (Edit / Delete)
 * - Integrated empty state & totals summary
 *
 * Props:
 * @param {string|React.ReactNode} title - Section title (e.g. "Line Items" or "Items Purchased")
 * @param {boolean} required - Whether section shows red asterisk
 * @param {Array} items - The current lines array
 * @param {boolean} isLocked - If true, form & edit/delete actions are hidden (read-only mode)
 * @param {number|null} editingIndex - Index of line currently being edited (or null)
 * @param {string} [editingTitle] - Header label when editing (e.g. "Editing Item #1")
 * @param {string} [addTitle] - Header label when adding (e.g. "Add Item" or "Add Consignment Line")
 * @param {Function} renderForm - Render function: ({ isEditing, editingIndex }) => form JSX
 * @param {Array} columns - Table column configs: [{ header, key, width, minWidth, align, render }]
 * @param {Function} onEdit - Callback (idx) => void
 * @param {Function} onDelete - Callback (idx) => void
 * @param {Function} onCancelEdit - Callback () => void
 * @param {Function} [onClear] - Callback () => void to reset the add form inputs
 * @param {string} [error] - Error message to display under header
 * @param {string} [emptyTitle] - Title for empty state
 * @param {string} [emptyMessage] - Subtext for empty state
 * @param {Function} [renderTotals] - Render function: (items) => totals JSX
 * @param {string} [maxTableHeight="32vh"] - Max scroll height for table body
 */
export default function LineItemsEditor({
  title = "Line Items",
  required = false,
  items = [],
  isLocked = false,
  editingIndex = null,
  editingTitle,
  addTitle = "Add Item",
  renderForm,
  columns = [],
  onEdit,
  onDelete,
  onCancelEdit,
  onClear,
  error,
  emptyTitle = "No items added yet",
  emptyMessage = "Use the form above to add items to this record.",
  renderTotals,
  maxTableHeight = "32vh",
}) {
  const isEditing = editingIndex !== null;

  return (
    <div className="rv-field" style={{ marginBottom: 12 }}>
      {/* ── Section Title & Item Count ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <label className="rv-label" style={{ marginBottom: 0 }}>
          {title} {required && <span className="rv-required">*</span>}
        </label>
        <span style={{ fontSize: 11.5, color: "var(--taupe)", fontWeight: 500 }}>
          {items.length} item{items.length !== 1 ? "s" : ""} added
        </span>
      </div>

      {error && <p className="rv-field-err" style={{ marginBottom: 8 }}>{error}</p>}

      {/* ── 1. Top Entry Form (Hidden when record is locked) ── */}
      {!isLocked && renderForm && (
        <div
          style={{
            background: isEditing ? "rgba(185,167,146,.12)" : "rgba(185,167,146,.05)",
            border: isEditing ? "1.5px solid var(--taupe, #b9a792)" : "1px solid rgba(185,167,146,.25)",
            borderRadius: 10,
            padding: "12px 14px",
            marginBottom: 12,
            transition: "all .2s ease",
          }}
        >
          {/* Form Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--dark)", textTransform: "uppercase", letterSpacing: ".5px" }}>
                {isEditing ? (editingTitle || `Editing Line #${editingIndex + 1}`) : addTitle}
              </span>
              {isEditing && (
                <span style={{ fontSize: 10, fontWeight: 600, color: "#8a5520", background: "rgba(232,202,184,.6)", padding: "1px 7px", borderRadius: 10 }}>
                  Editing Row
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {isEditing && onCancelEdit && (
                <button
                  type="button"
                  className="rv-btn rv-btn--ghost"
                  style={{ padding: "2px 8px", fontSize: 11, height: "auto" }}
                  onClick={onCancelEdit}
                >
                  Cancel Edit
                </button>
              )}
              {!isEditing && onClear && (
                <button
                  type="button"
                  className="rv-btn rv-btn--ghost"
                  style={{ padding: "2px 8px", fontSize: 11, height: "auto" }}
                  onClick={onClear}
                  title="Clear form inputs"
                >
                  Clear Form
                </button>
              )}
            </div>
          </div>

          {/* Form Inputs & Action Button (rendered via caller) */}
          {renderForm({ isEditing, editingIndex, onClear, onCancelEdit })}
        </div>
      )}

      {/* ── 2. Items Table / Grid ── */}
      <div
        style={{
          border: "1px solid rgba(185,167,146,.25)",
          borderRadius: 10,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        {/* Table Subheader Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            background: "rgba(185,167,146,.08)",
            borderBottom: "1px solid rgba(185,167,146,.18)",
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--taupe-dark, #5a4d44)", textTransform: "uppercase", letterSpacing: ".5px" }}>
            Items List ({items.length})
          </span>
        </div>

        {items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 16px", color: "var(--taupe)" }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>{emptyTitle}</p>
            <p style={{ margin: "4px 0 0", fontSize: 11.5 }}>{emptyMessage}</p>
          </div>
        ) : (
          <div className="rv-table-wrap" style={{ maxHeight: maxTableHeight, overflowY: "auto" }}>
            <table className="rv-table" style={{ width: "100%", margin: 0, fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 34, textAlign: "center", paddingLeft: 8 }}>#</th>
                  {columns.map((col, cIdx) => (
                    <th
                      key={cIdx}
                      style={{
                        width: col.width,
                        minWidth: col.minWidth,
                        textAlign: col.align || "left",
                      }}
                    >
                      {col.header}
                    </th>
                  ))}
                  {!isLocked && (
                    <th style={{ width: 80, minWidth: 80, textAlign: "center", paddingRight: 8 }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const isRowEditing = editingIndex === idx;

                  return (
                    <tr
                      key={idx}
                      style={{
                        background: isRowEditing ? "rgba(185,167,146,.18)" : undefined,
                        transition: "background .15s ease",
                      }}
                    >
                      <td style={{ textAlign: "center", paddingLeft: 8, fontWeight: 600, color: "var(--taupe)", fontSize: 11 }}>
                        {idx + 1}
                      </td>
                      {columns.map((col, cIdx) => (
                        <td
                          key={cIdx}
                          style={{
                            textAlign: col.align || "left",
                            width: col.width,
                            minWidth: col.minWidth,
                          }}
                        >
                          {col.render ? col.render(item, idx) : item[col.key]}
                        </td>
                      ))}
                      {!isLocked && (
                        <td style={{ textAlign: "center", paddingRight: 8, whiteSpace: "nowrap", width: 80, minWidth: 80 }}>
                          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                            {onEdit && (
                              <button
                                type="button"
                                className="rv-action-btn rv-action-btn--edit"
                                style={{ width: 28, height: 28, padding: 0 }}
                                title="Edit item"
                                onClick={() => onEdit(idx)}
                              >
                                <EditIcon />
                              </button>
                            )}
                            {onDelete && (
                              <button
                                type="button"
                                className="rv-action-btn rv-action-btn--del"
                                style={{ width: 28, height: 28, padding: 0 }}
                                title="Remove item"
                                onClick={() => onDelete(idx)}
                              >
                                <TrashIcon />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── 3. Optional Grand Totals Strip ── */}
      {renderTotals && items.length > 0 && renderTotals(items)}
    </div>
  );
}

